(() => {
  'use strict';
  const key = Symbol.for('limitless.caravanGallery.v1');
  if (window[key]) {
    window[key].initialize(document);
    return;
  }
  const selector = '[data-lbp-caravan-gallery]';
  const instances = new WeakMap();
  const galleries = scope => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];
  const initialize = scope => {
    galleries(scope).forEach(gallery => {
      if (instances.has(gallery)) return;
      const track = gallery.querySelector('[data-caravan-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-caravan-slide]'));
      const controls = gallery.querySelector('[data-caravan-controls]');
      const previous = gallery.querySelector('[data-caravan-previous]');
      const next = gallery.querySelector('[data-caravan-next]');
      const counter = gallery.querySelector('[data-caravan-counter]');
      if (!track || !slides.length || !controls || !previous || !next || !counter) return;
      let current = 0;
      let requested = null;
      let frame = null;
      let settle = null;
      let width = track.clientWidth;
      const listeners = [];
      const on = (element, event, handler, options) => {
        element.addEventListener(event, handler, options);
        listeners.push(() => element.removeEventListener(event, handler, options));
      };
      const paint = index => {
        current = index;
        const text = counter.dataset.caravanCounter.replace('{current}', String(index + 1)).replace('{total}', String(slides.length));
        if (counter.textContent !== text) counter.textContent = text;
        previous.setAttribute('aria-disabled', String(index === 0));
        next.setAttribute('aria-disabled', String(index === slides.length - 1));
      };
      const nearest = () => {
        const box = track.getBoundingClientRect();
        const center = box.left + box.width / 2;
        let distance = Infinity;
        let result = current;
        slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const difference = Math.abs(rect.left + rect.width / 2 - center);
          if (difference < distance) { distance = difference; result = index; }
        });
        return result;
      };
      const move = (index, animate = true) => {
        const destination = Math.max(0, Math.min(slides.length - 1, index));
        requested = destination;
        const trackBox = track.getBoundingClientRect();
        const slideBox = slides[destination].getBoundingClientRect();
        const left = track.scrollLeft + slideBox.left + slideBox.width / 2 - trackBox.left - trackBox.width / 2;
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({ left, behavior: animate && !reduced ? 'smooth' : 'instant' });
        paint(destination);
      };
      on(previous, 'click', () => move((requested ?? current) - 1));
      on(next, 'click', () => move((requested ?? current) + 1));
      on(track, 'keydown', event => {
        if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const direction = window.getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        const index = requested ?? current;
        const destinations = { ArrowLeft: index - direction, ArrowRight: index + direction, Home: 0, End: slides.length - 1 };
        if (!Object.prototype.hasOwnProperty.call(destinations, event.key)) return;
        event.preventDefault();
        move(destinations[event.key]);
      });
      const release = () => { requested = null; };
      on(track, 'pointerdown', release, { passive: true });
      on(track, 'wheel', release, { passive: true });
      on(track, 'scroll', () => {
        if (frame === null) frame = window.requestAnimationFrame(() => { frame = null; paint(nearest()); });
        window.clearTimeout(settle);
        settle = window.setTimeout(() => { settle = null; requested = null; paint(nearest()); }, 160);
      }, { passive: true });
      const resize = window.ResizeObserver ? new ResizeObserver(() => {
        if (track.clientWidth !== width) {
          width = track.clientWidth;
          move(current, false);
          requested = null;
        }
      }) : null;
      resize?.observe(track);
      controls.hidden = slides.length < 2;
      paint(nearest());
      instances.set(gallery, () => {
        listeners.forEach(remove => remove());
        resize?.disconnect();
        if (frame !== null) window.cancelAnimationFrame(frame);
        window.clearTimeout(settle);
        controls.hidden = true;
        instances.delete(gallery);
      });
    });
  };
  window[key] = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => galleries(event.target).forEach(gallery => instances.get(gallery)?.()));
})();

